import { useState, type FormEvent } from "react";
import { api, API, ApiError } from "../api";
import { ErrorNote, formatDate, Loading, Moment, useApi } from "../components/ui";
import { useMe } from "../App";

interface Invoice {
  id: string;
  orgId: string;
  orgName: string;
  number: string;
  period: string;
  amountCents: number;
  currency: string;
  status: "issued" | "paid" | "void";
  issuedAt: string;
  dueAt: string | null;
}

const money = (cents: number, currency: string) => new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);

export function Invoices() {
  const { me } = useMe();
  const staff = me.role === "staff";
  const { data, error, loading, reload } = useApi<{ invoices: Invoice[] }>("/invoices");
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{staff ? "All customers" : me.orgName}</p>
          <h1>Invoices</h1>
        </div>
      </div>
      <ErrorNote message={error} />
      {staff && <UploadInvoice onDone={reload} />}
      {loading && !data ? (
        <Loading />
      ) : data && data.invoices.length === 0 ? (
        <Moment title="No invoices yet">
          <p className="muted">Monthly invoices will appear here to download as PDFs once billing starts.</p>
        </Moment>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                {staff && <th>Customer</th>}
                <th>Period</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Download</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.invoices.map((v) => (
                <tr key={v.id}>
                  <td className="mono small">{v.number}</td>
                  {staff && <td>{v.orgName}</td>}
                  <td>{v.period}</td>
                  <td>{formatDate(v.issuedAt)}</td>
                  <td>{formatDate(v.dueAt)}</td>
                  <td className="num">{money(v.amountCents, v.currency)}</td>
                  <td>
                    {staff ? (
                      <select
                        aria-label={`Status of ${v.number}`}
                        value={v.status}
                        onChange={(e) => api(`/staff/invoices/${v.id}`, { method: "PATCH", json: { status: e.target.value } }).then(reload)}
                        style={{ minWidth: 100 }}
                      >
                        <option value="issued">Issued</option>
                        <option value="paid">Paid</option>
                        <option value="void">Void</option>
                      </select>
                    ) : (
                      <span className={`chip ${v.status === "paid" ? "chip-resolved" : v.status === "void" ? "chip-closed" : "chip-open"}`}>
                        {v.status === "paid" ? "Paid" : v.status === "void" ? "Void" : "Issued"}
                      </span>
                    )}
                  </td>
                  <td>
                    <a className="btn btn-small" href={`${API}/invoices/${v.id}/pdf`}>
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UploadInvoice({ onDone }: { onDone: () => void }) {
  const { data: orgs } = useApi<{ orgs: Array<{ id: string; name: string; archived: number }> }>("/staff/orgs");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/staff/invoices", { method: "POST", body: new FormData(e.currentTarget) });
      setOpen(false);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <p>
        <button className="btn" onClick={() => setOpen(true)}>
          Upload an invoice
        </button>
      </p>
    );
  }
  return (
    <form className="card stack" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h2>Upload invoice</h2>
      <div className="grid grid-3">
        <div className="field">
          <label htmlFor="inv-org">Customer</label>
          <select id="inv-org" name="orgId" required>
            {orgs?.orgs.filter((o) => !o.archived).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="inv-num">Invoice number</label>
          <input id="inv-num" name="number" type="text" required maxLength={40} />
        </div>
        <div className="field">
          <label htmlFor="inv-period">Period</label>
          <input id="inv-period" name="period" type="text" required placeholder="October 2026" maxLength={40} />
        </div>
        <div className="field">
          <label htmlFor="inv-amt">Amount (USD)</label>
          <input id="inv-amt" name="amount" type="number" min="0" step="0.01" required />
        </div>
        <div className="field">
          <label htmlFor="inv-issued">Issued</label>
          <input id="inv-issued" name="issuedAt" type="date" required defaultValue={new Date().toISOString().slice(0, 10)} />
        </div>
        <div className="field">
          <label htmlFor="inv-due">Due</label>
          <input id="inv-due" name="dueAt" type="date" />
        </div>
      </div>
      <div className="field">
        <label htmlFor="inv-file">PDF</label>
        <input id="inv-file" name="file" type="file" accept="application/pdf" required />
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}
      <div className="row">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? "Uploading…" : "Upload"}
        </button>
        <button type="button" className="btn" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
